import React, { useEffect, useState } from "react";
import {
  checkAuthStatus as svcCheckAuthStatus,
  getRepositories,
  getBranches,
  getFiles,
  loginRequest,
  logout as svcLogout,
  Repo,
  Branch,
  FileItem,
} from "../services/gitService";


// ---------------- COMPONENT ----------------
export default function GitRepoBrowser({ handleExistingPolicySelect,onProceed }) {
  const [loggedIn, setLoggedIn] = useState<boolean | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  const [repos, setRepos] = useState<Repo[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [files, setFiles] = useState<FileItem[]>([]);

  const [selectedRepo, setSelectedRepo] = useState<string | null>(null);
  const [selectedBranch, setSelectedBranch] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<string>("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Local wrapper around service auth check
  const checkAuthStatus = async () => {
    try {
      setAuthLoading(true);
      setAuthError(null);
      const res = await svcCheckAuthStatus();
      setLoggedIn(res.authenticated);
      if (!res.authenticated) {
        setRepos([]);
        setBranches([]);
        setFiles([]);
        await svcLogout();
      }
    } catch (err) {
      console.error("Auth check failed:", err);
      setAuthError(err instanceof Error ? err.message : "Authentication check failed");
      setLoggedIn(false);
    } finally {
      setAuthLoading(false);
    }
  };

  // ---------------- AUTH CHECK ----------------
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // ---------------- LOAD REPOS ----------------
  useEffect(() => {
    if (!loggedIn) return;

    setLoading(true);
    getRepositories()
      .then((res) => setRepos(res.repositories))
      .catch(async () => {
        setError("Failed to load repositories");
        try {
          await svcLogout();
        } catch (_) {}
      })
      .finally(() => setLoading(false));
  }, [loggedIn]);

  // ---------------- LOAD BRANCHES ----------------
  useEffect(() => {
    if (!selectedRepo) return;

    setSelectedBranch(null);
    setSelectedFile(null);
    setFiles([]);
    const repo_url = repos.filter((r) => r.name === selectedRepo);
    setLoading(true);
    getBranches(repo_url[0].http_url_to_repo)
      .then((res) => setBranches(res.branches))
      .catch(() => setError("Failed to load branches"))
      .finally(() => setLoading(false));
  }, [selectedRepo]);

  // ---------------- LOAD FILES ---------------- 
  useEffect(() => {
    if (!selectedRepo || !selectedBranch) return;

    setSelectedFile(null);
    setFileContent("");

    setLoading(true);
    const repo_url = repos.filter((r) => r.name === selectedRepo);
    getFiles(repo_url[0].http_url_to_repo, selectedBranch)
      .then((res) => setFiles(res.files))
      .catch(() => setError("Failed to load files"))
      .finally(() => setLoading(false));
  }, [selectedRepo, selectedBranch]);

  // ---------------- LOAD FILE CONTENT ----------------
  useEffect(() => {
    if (!selectedRepo || !selectedBranch || !selectedFile) return;

    setLoading(true);
    const repo_url = repos.filter((r) => r.name === selectedRepo);
    const path = files.filter((f) => f.name === selectedFile);

    // Call the handler with file details
    if (typeof handleExistingPolicySelect === 'function') {
      handleExistingPolicySelect(repo_url[0].http_url_to_repo, selectedBranch, path[0].path)
        .then((res) => setFileContent(res))
        .catch(() => setError("Failed to load file content"))
        .finally(() => setLoading(false));
    }
  }, [selectedFile, selectedRepo, selectedBranch, repos, files]);

  // ---------------- LOGIN HANDLER ----------------
  const LoginwithGit = async () => {
    try {
      setAuthError(null);
      const data = await loginRequest();
      const loginWindow = window.open(data.authorization_url, "gitlab_login", "width=600,height=700");
      
      // Check auth status every 2 seconds while popup is open
      const pollInterval = setInterval(async () => {
        if (loginWindow && loginWindow.closed) {
          clearInterval(pollInterval);
          // User closed the popup, check auth status
          setTimeout(() => {
            checkAuthStatus();
          }, 500);
        }
      }, 2000);

      // Also check after 10 seconds as a fallback
      setTimeout(() => {
        clearInterval(pollInterval);
        checkAuthStatus();
      }, 10000);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Login failed";
      setAuthError(errorMsg);
      console.error("Login error:", err);
    }
  };

  // ---------------- LOADING STATE ----------------
  if (authLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // ---------------- LOGIN SCREEN ----------------
  if (!loggedIn) {
    return (
      <div className="flex flex-col items-center justify-start  p-4 pt-4" style={{ height: "100%" }}>
        <div className="bg-white rounded-2xl shadow-xl p-4 w-80 mt-0 w-[70%]">
          <div className="text-center mb-3">
            <div className="text-3xl mb-2">📁</div>
            <h1 className="text-lg font-bold text-gray-900">Git File Explorer</h1>
            <p className="text-xs text-gray-600">Browse and manage files from your repositories</p>
          </div>

          {authError && (
            <div className="mb-2 p-1.5 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-xs text-red-700">{authError}</p>
            </div>
          )}

          <button
            onClick={() => LoginwithGit()}
            className="w-full px-4 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-md hover:shadow-lg text-xs"
          >
            Login with GitLab
          </button>

          <button
            onClick={() => checkAuthStatus()}
            className="w-full mt-1.5 px-4 py-1.5 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-all duration-200 text-xs"
          >
            ↻ Check Authentication Status
          </button>

          <p className="text-xs text-gray-500 text-center mt-3 leading-tight">
            Login via popup, then click "Check Status"
          </p>
        </div>
      </div>
    );
  }

  // Build breadcrumb path
  const breadcrumbPath = [
    selectedRepo && { label: selectedRepo, type: "repo" },
    selectedBranch && { label: selectedBranch, type: "branch" },
    selectedFile && { label: selectedFile, type: "file" },
  ].filter((item): item is { label: string; type: string } => Boolean(item));

  return (      
    <div className="h-screen flex flex-col">
      {/* HEADER */}
      <header className="bg-white border-b border-gray-200 px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">📁</span>
            <h1 className="text-xl font-bold text-gray-900">Git Repository Browser</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => checkAuthStatus()}
              className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Check authentication status and refresh"
            >
              ↻ Refresh Auth
            </button>
           {/*  <button
              onClick={() => window.location.reload()}
              className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh page"
            >
              ↻ Refresh
            </button> */}
          </div>
        </div>

        {/* BREADCRUMB */}
        {breadcrumbPath.length > 0 && (
          <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
            <span className="text-gray-400">Path:</span>
            {breadcrumbPath.map((item, idx) => (
              <React.Fragment key={idx}>
                {idx > 0 && <span className="text-gray-300">/</span>}
                <span className="text-gray-700 font-medium">{item!.label}</span>
              </React.Fragment>
            ))}
          </div>
        )}
      </header>

      {/* MAIN CONTENT */}
      <div className="flex-1 overflow-hidden flex gap-4 p-4">
        {/* SIDEBAR - REPOS & BRANCHES */}
        <aside className="w-64 flex flex-col gap-4">
          {/* REPOSITORIES */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden flex flex-col h-1/2">
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-4 py-3 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900 text-sm">📚 Repositories</h2>
              {repos.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">{repos.length} repositories</p>
              )}
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading && !repos.length && (
                <div className="p-4 text-center">
                  <p className="text-sm text-gray-400">Loading repositories...</p>
                </div>
              )}
              {repos.length === 0 && !loading && (
                <div className="p-4 text-center">
                  <p className="text-sm text-gray-400">No repositories found</p>
                </div>
              )}
              {repos.map((repo) => (
                <div
                  key={repo.id}
                  className={`px-4 py-3 cursor-pointer border-l-4 transition-all duration-150 ${
                    selectedRepo === repo.name
                      ? "bg-blue-50 border-blue-600 text-blue-900 font-medium"
                      : "border-transparent hover:bg-gray-50 text-gray-700"
                  }`}
                  onClick={() => {
                    setSelectedRepo(repo.name);
                    setSelectedBranch(null);
                    setSelectedFile(null);
                    setFileContent("");
                  }}
                >
                  <div className="text-sm">{repo.name}</div>
                </div>
              ))}
            </div>
          </div>

          {/* BRANCHES */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden flex flex-col h-1/2">
            <div className="bg-gradient-to-r from-green-50 to-green-100 px-4 py-3 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900 text-sm">🌿 Branches</h2>
              {branches.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">{branches.length} branches</p>
              )}
            </div>
            <div className="flex-1 overflow-y-auto">
              {!selectedRepo && (
                <div className="p-4 text-center">
                  <p className="text-sm text-gray-400">Select a repository</p>
                </div>
              )}
              {selectedRepo && branches.length === 0 && !loading && (
                <div className="p-4 text-center">
                  <p className="text-sm text-gray-400">No branches found</p>
                </div>
              )}
              {branches.map((branch) => (
                <div
                  key={branch.name}
                  className={`px-4 py-3 cursor-pointer border-l-4 transition-all duration-150 ${
                    selectedBranch === branch.name
                      ? "bg-green-50 border-green-600 text-green-900 font-medium"
                      : "border-transparent hover:bg-gray-50 text-gray-700"
                  }`}
                  onClick={() => {
                    setSelectedBranch(branch.name);
                    setSelectedFile(null);
                    setFileContent("");
                  }}
                >
                  <div className="text-sm">{branch.name}</div>
                </div>
              ))}
            </div>
          </div>
        </aside>

        {/* MAIN CONTENT AREA - FILES & PREVIEW */}
        <div className="flex-1 flex gap-4 min-w-0">
          {/* FILES LIST */}
          <div className="flex-1 bg-white rounded-lg shadow-sm overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-purple-50 to-purple-100 px-4 py-3 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900 text-sm">📄 Files</h2>
              {files.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">{files.length} items</p>
              )}
            </div>
            <div className="flex-1 overflow-y-auto">
              {!selectedBranch && (
                <div className="p-6 text-center">
                  <p className="text-gray-400">Select a branch to view files</p>
                </div>
              )}
              {selectedBranch && files.length === 0 && !loading && (
                <div className="p-6 text-center">
                  <p className="text-gray-400">No files found</p>
                </div>
              )}
              {loading && (
                <div className="p-6 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-2"></div>
                  <p className="text-gray-400">Loading...</p>
                </div>
              )}
              {files.map((file) => (
                <div
                  key={file.name}
                  className={`px-4 py-3 cursor-pointer border-l-4 transition-all duration-150 flex items-center gap-2 ${
                    selectedFile === file.name && file.type !== "dir"
                      ? "bg-purple-50 border-purple-600 text-purple-900 font-medium"
                      : "border-transparent hover:bg-gray-50 text-gray-700"
                  } ${file.type === "dir" ? "opacity-75 cursor-not-allowed" : ""}`}
                  onClick={() => {
                    if (file.type === "dir") return; // Ignore clicks on directories
                    setSelectedFile(file.name);
                  }}
                >
                  <span className="text-lg">{file.type === "dir" ? "📁" : "📝"}</span>
                  <span className="text-sm">{file.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* FILE PREVIEW */}
          <div className="flex-1 bg-white rounded-lg shadow-sm overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-orange-50 to-orange-100 px-4 py-3 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900 text-sm">👁️ Preview</h2>
              {selectedFile && (
                <p className="text-xs text-gray-500 mt-1">{selectedFile}</p>
              )}
            </div>
            <div className="flex-1 overflow-auto p-4">
              {!selectedFile && (
                <div className="text-center text-gray-400 mt-10">
                  <p className="text-lg">Select a file to preview</p>
                </div>
              )}
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
              {loading && selectedFile && (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto mb-2"></div>
                    <p className="text-gray-400">Loading file content...</p>
                  </div>
                </div>
              )}
              {fileContent && (
                <pre className="text-xs bg-gray-900 text-gray-100 p-4 rounded-lg overflow-auto max-h-full font-mono leading-relaxed">
                  {fileContent}
                </pre>
              )}
            </div>
          </div>
        </div>
         <button
              className="px-4 py-2 text-white rounded bg-primary"
              onClick={onProceed}
            >
              Next
            </button>
      </div>
    </div>
  )};
